/**
 * @file SymbolRegistry.ts
 * @role Loads + validates the symbol registry (symbols.config.json) and answers "what
 *       is symbol X?" (identity, value tier) and "which symbols exist?". Validation is
 *       a trust boundary, same as backend data — a malformed registry fails loud at boot.
 * @owns The id -> SymbolDef map and ordered id list.
 * @reads Bundled symbols.config.json (default) or injected raw config (tests).
 * @neverDoes Render. Decide outcomes. Pick which symbols land (that's the RGS result).
 * @invariants 10 (validate before use).
 * @see docs/REEL_SYSTEM.md, docs/CONFIG_GUIDE.md
 */
import { z } from 'zod';
import symbolsConfigRaw from '@config/symbols.config.json';
import { GameError } from '@core/errors/GameError';
import type { SymbolId } from './SymbolId';

export const SymbolDefSchema = z.object({
  id: z.string().min(1),
  /** Free-form value tier (e.g. low/mid/high/special); drives placeholder color for now. */
  tier: z.string().min(1),
});
export type SymbolDef = z.infer<typeof SymbolDefSchema>;

const SymbolsConfigSchema = z.object({
  symbols: z.array(SymbolDefSchema).min(1),
});

export class SymbolRegistry {
  private readonly byId: Map<SymbolId, SymbolDef>;
  private readonly order: SymbolId[];

  constructor(raw: unknown = symbolsConfigRaw) {
    const parsed = SymbolsConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError('CONFIG_LOAD_FAILED', `Invalid symbols config: ${parsed.error.message}`, {
        cause: parsed.error,
      });
    }
    this.byId = new Map(parsed.data.symbols.map((symbol) => [symbol.id, symbol]));
    this.order = parsed.data.symbols.map((symbol) => symbol.id);
  }

  get(id: SymbolId): SymbolDef | undefined {
    return this.byId.get(id);
  }

  /** Like get(), but throws if the id is unknown — use when the id must be valid. */
  require(id: SymbolId): SymbolDef {
    const def = this.byId.get(id);
    if (!def) {
      throw new GameError('UNKNOWN_ERROR', `Unknown symbol id: ${id}`);
    }
    return def;
  }

  ids(): readonly SymbolId[] {
    return this.order;
  }
}
